import React, { useCallback, useEffect, useState } from 'react'
import AccountLayout from './AccountLayout'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { getCustomers } from 'services/customer.service'
import { MediumPrimaryButton } from 'components/Utilities/button'
import './accountManageUsers.css'
import Dialog from 'components/Utilities/Dialog'
import { LargePrimaryButton } from 'components/Utilities/button'
import Dropdown from 'components/Utilities/dropdown'
import {
  assignGroup,
  getUsersFromGroup,
  reassignGroup,
} from 'services/iam.service'
import { createCustomer, updateCustomer } from 'services/customer.service'
import { useCurrency } from 'context/currency-context'
import { useLegalEntity } from 'context/legal-entity-provider'
import PropTypes from 'prop-types'
import { useAuth } from 'context/auth-provider'

const REQUIRED_FIELDS = {
  firstName: true,
  lastName: true,
  title: false,
  active: false,
  contactEmail: true,
}

const INITIAL_FORM_DATA = {
  firstName: '',
  lastName: '',
  title: '',
  active: false,
  contactEmail: '',
  groups: [],
}

function isValidEmail(email) {
  return /\S+@\S+\.\S+/.test(email)
}

// User form component
const UserForm = ({ customer, mode, onSave, onCancel }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA)
  const { currentLegalEntity } = useLegalEntity()

  useEffect(() => {
    if (customer) {
      setFormData(customer)
    }
  }, [customer])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prevState) => {
      if (type === 'checkbox') {
        return {
          ...prevState,
          [name]: checked,
        }
      }
      return {
        ...prevState,
        [name]: value,
      }
    })
  }

  const hasError = useCallback(
    (key) => {
      return (
        REQUIRED_FIELDS[key] &&
        (formData[key] === undefined || formData[key]?.length === 0)
      )
    },
    [formData]
  )

  const handleSubmit = () => {
    onSave(formData)
  }

  const isFormValid = () => {
    return (
      formData.firstName &&
      formData.lastName &&
      formData.contactEmail &&
      isValidEmail(formData?.contactEmail)
    )
  }

  return (
    <div className="upsertUserDialog">
      <div className="text-xl font-bold mb-[10px]">
        {mode === 'CREATE' ? 'Create New User' : "Edit User's Detail"}
      </div>
      <div className="dialog-user-contenet">
        <div className="dialog-user-input">
          <div className="mb-1">Title:</div>
          <input
            type="text"
            name="title"
            disabled={mode === 'UPDATE'}
            value={formData?.title}
            onChange={handleChange}
            className={`mt-2 sm:mt-0 form-input-item border-gray`}
            style={{
              borderColor: hasError('title') && 'red',
            }}
          />
          {hasError('title') && (
            <div className="text-red-500 text-xs">Must not be blank</div>
          )}
        </div>
        <div className="dialog-user-input">
          <div className="mb-1">Active:</div>
          <div className={`mt-2 sm:mt-0 form-input-item border-gray`}>
            <input
              type="checkbox"
              className="dialog-user-checkbox"
              name="active"
              value={formData?.active}
              checked={formData?.active}
              onChange={handleChange}
            />
            <span className="dialog-user-active">
              User activated if selected
            </span>
          </div>
        </div>
        <div className="dialog-user-input">
          <div className="mb-1">First Name:</div>
          <input
            type="text"
            name="firstName"
            disabled={mode === 'UPDATE'}
            value={formData?.firstName}
            onChange={handleChange}
            className={`mt-2 sm:mt-0 form-input-item border-gray`}
            style={{
              borderColor: hasError('firstName') && 'red',
            }}
          />
          {hasError('firstName') && (
            <div className="text-red-500 text-xs">Must not be blank</div>
          )}
        </div>
        <div className="dialog-user-input">
          <div className="mb-1">Last Name:</div>
          <input
            type="text"
            name="lastName"
            disabled={mode === 'UPDATE'}
            value={formData?.lastName}
            onChange={handleChange}
            className={`mt-2 sm:mt-0 form-input-item border-gray`}
            style={{
              borderColor: hasError('lastName') && 'red',
            }}
          />
          {hasError('lastName') && (
            <div className="text-red-500 text-xs">Must not be blank</div>
          )}
        </div>
        <div className="dialog-user-input dialog-user-input-role">
          <label className="mb-1">User rights:</label>
          <Dropdown
            searchable={false}
            multi={true}
            options={currentLegalEntity.customerGroups.map((role) => {
              return {
                key: role.id,
                label: role.name.en,
                value: role,
              }
            })}
            defaultValue={formData?.groups?.map(group => ({
              key: group.id,
              label: group.name.en,
              value: group,
            }))}
            onChange={(selectedGroups) => {
              setFormData((prev) => {
                return {
                  ...prev,
                  groups: selectedGroups.map(g => g.value)
                }
              })
            }}
            style={{
              borderWidth: '1px',
              width: '438px',
            }}
          />
        </div>
        <div className="dialog-user-input">
          <div className="mb-1">Email address:</div>
          <input
            type="text"
            name="contactEmail"
            disabled={mode === 'UPDATE'}
            value={formData?.contactEmail}
            onChange={handleChange}
            className={`mt-2 sm:mt-0 form-input-item border-gray`}
            style={{
              borderColor: hasError('contactEmail') && 'red',
            }}
          />
          {(hasError('contactEmail') || !isValidEmail(formData?.contactEmail)) && (
            <div className="text-red-500 text-xs">Correct email must be provided</div>
          )}
        </div>
      </div>
      <div className="dialog-user-footer">
        <LargePrimaryButton
          className="dialog-user-btn bg-yellow"
          title={mode === 'CREATE' ? 'CREATE NEW USER' : 'SAVE'}
          disabled={!isFormValid()}
          onClick={handleSubmit}
        />
        <LargePrimaryButton
          className="dialog-user-btn dialog-user-cancel-btn"
          title={'CANCEL'}
          onClick={onCancel}
        />
      </div>
    </div>
  )
}

UserForm.propTypes = {
  customer: PropTypes.object,
  mode: PropTypes.oneOf(['CREATE', 'UPDATE']).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

const UserDialog = ({ open, onClose, customer, mode, onSave }) => {
  return (
    <Dialog
      maxWidth="xl"
      open={open}
      onClose={onClose}
    >
      <UserForm 
        customer={customer} 
        mode={mode} 
        onSave={onSave} 
        onCancel={onClose} 
      />
    </Dialog>
  )
}

UserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  mode: PropTypes.oneOf(['CREATE', 'UPDATE']).isRequired,
  onSave: PropTypes.func.isRequired,
}

// User table component
const UserTable = ({ users, onEditUser }) => {
  return (
    <TableContainer className="desktop_only">
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell align="left" className="!font-bold grid-column-title">
              First Name
            </TableCell>
            <TableCell align="left" className="!font-bold grid-column-title">
              Surname
            </TableCell>
            <TableCell align="left" className="!font-bold grid-column-title">
              Groups
            </TableCell>
            <TableCell align="left" className="!font-bold grid-column-title">
              Status
            </TableCell>
            <TableCell align="left" />
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((row) => (
            <TableRow
              key={row.id}
              className="hover:bg-slate-100"
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell align="left" className="!font-bold !py-6">
                {`${row.firstName}`}
              </TableCell>
              <TableCell align="left" className="!font-bold !py-6">
                {`${row.lastName}`}
              </TableCell>
              <TableCell align="left" className="!font-bold !py-6">
                {row?.groups?.map((group) => group?.name?.en).join(', ')}
              </TableCell>
              <TableCell align="left" className="!font-bold !py-6">
                {row.active ? 'Active' : 'Inactive'}
              </TableCell>
              <TableCell align="left" className="!py-6">
                <div className="flex">
                  <div className="font-inter font-semibold text-[14px] underline ml-6">
                    <span
                      onClick={() => onEditUser(row)}
                      className="cursor-pointer"
                    >
                      Edit
                    </span>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

UserTable.propTypes = {
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    groups: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.shape({
        en: PropTypes.string
      })
    })),
    active: PropTypes.bool
  })).isRequired,
  onEditUser: PropTypes.func.isRequired,
}

const assignUserToGroups = async (userId, groups) => {
  return Promise.all(groups.map(group => 
    assignGroup(group.id, userId)
  ))
}

const updateUserGroups = async (userId, currentGroups, newGroups) => {
  const groupsToRemove = currentGroups.filter(g => 
    !newGroups.some(ng => ng.id === g.id)
  )
  await Promise.all(groupsToRemove.map(group =>
    reassignGroup(group.id, null, userId)
  ))
  
  const groupsToAdd = newGroups.filter(g => 
    !currentGroups.some(cg => cg.id === g.id)
  )
  await Promise.all(groupsToAdd.map(group =>
    assignGroup(group.id, userId)
  ))
}

export const CompanyCustomers = () => {
  const [customers, setCustomers] = useState([])
  const [showDialog, setShowDialog] = useState(false)
  const [currentCustomer, setCurrentCustomer] = useState(null)
  const [mode, setMode] = useState('CREATE')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const { activeCurrency } = useCurrency()
  const { currentLegalEntity } = useLegalEntity()
  const { hasScope } = useAuth()
  

  const fetchGroupUsers = useCallback(async () => {
    try {
      const groups = (
        await Promise.all(
          currentLegalEntity.customerGroups.map(async (group) => {
            const groupUsers = await getUsersFromGroup(group.id)
            return groupUsers.map((g) => ({
              ...g,
              group: group,
            }))
          })
        )
      ).flatMap((group) => group)
      
      return groups
    } catch (err) {
      console.error('Error fetching group users:', err)
      throw err
    }
  }, [currentLegalEntity])

  const getCompanyCustomers = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const groups = await fetchGroupUsers()
      const fetchedCustomers = await getCustomers()
      
      const effectiveCustomers = fetchedCustomers
        .filter((customer) => {
          return groups.filter((group) => group.userId === customer.id).length > 0
        })
        .map((customer) => ({
          ...customer,
          group: groups.find((group) => group.userId === customer.id).group,
          groups: groups.filter((group) => group.userId === customer.id).map((g) => g.group)
        }))
      
      setCustomers(effectiveCustomers)
    } catch (err) {
      console.error('Error fetching customers:', err)
      setError('Failed to load customers. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }, [fetchGroupUsers])

  const handleEditCustomer = useCallback((customer) => {
    setCurrentCustomer(customer)
    setShowDialog(true)
    setMode('UPDATE')
  }, [])

  const handleCreateCustomer = () => {
    setCurrentCustomer({
      ...INITIAL_FORM_DATA
    })
    setShowDialog(true)
    setMode('CREATE')
  }

  const handleCloseDialog = () => {
    setShowDialog(false)
    setCurrentCustomer(null)
    setError(null)
  }

  const handleSaveCustomer = async (formData) => {
    setIsLoading(true)
    setError(null)
    
    try {
      if (mode === 'CREATE') {
        const newCustomer = await createCustomer(formData, activeCurrency)
        if (!formData.active) {
          await updateCustomer(newCustomer.id, formData.active)
        }
        formData.id = newCustomer.id
        await assignUserToGroups(newCustomer.id, formData.groups)
        setCustomers(prev => [...prev, formData])
      } else if (mode === 'UPDATE') {
        if (currentCustomer.active !== formData.active) {
          await updateCustomer(currentCustomer.id, formData.active)
        }
        
        await updateUserGroups(
          currentCustomer.id, 
          currentCustomer.groups || [], 
          formData.groups || []
        )
        
        setCustomers(prev => {
          const index = prev.findIndex(c => c.id === currentCustomer.id)
          if (index === -1) return prev
          
          const updated = [...prev]
          updated[index] = formData
          return updated
        })
      }
      
      handleCloseDialog()
    } catch (err) {
      console.error('Error saving customer:', err)
      setError('Failed to save customer. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getCompanyCustomers()
  }, [getCompanyCustomers])

  return (
    !hasScope('iam.user_read_own') && !isLoading ? (
      <div className="p-4 text-center">
      <p className="text-red-600 font-medium">You do not have permission to manage users.</p>
    </div>
    ) : (
      <div className="md:mt-[60px]">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <UserDialog
        open={showDialog}
        onClose={handleCloseDialog}
        customer={currentCustomer}
        mode={mode}
        onSave={handleSaveCustomer}
      />
      
      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
            <>
              <UserTable 
                users={customers} 
                onEditUser={handleEditCustomer} 
              />
              
              <div className="create-customer-wrapper">
                <MediumPrimaryButton
                  className="cta-button bg-yellow"
                  title="CREATE NEW USER"
                  onClick={handleCreateCustomer}
                />
              </div>
            </>
         
        </>
      )}
    </div>  
    ) 
  )
}

const AccountManageUsers = () => {
  return (
    <AccountLayout page="Manage Users">
      <CompanyCustomers />
    </AccountLayout>
  )
}

export default AccountManageUsers
